import { Controller } from "@hotwired/stimulus"
import { lockPage, unlockPage } from "../lib/page_lock"

// Connects to data-controller="mobile-menu" on layout v2's <body>.
//
// The phone menu: a dark bottom sheet the dock's menu button lifts over the
// page, server-rendered and hidden until then. The scrim, Escape and the button
// close it; a navigation replaces the body, so the next page opens closed.
// Editing (?side_menu=edit renders the sheet as the editor) opens it on load
// and holds it open: Done is a navigation, and the way out.
const SLIDE_MS = 250
// side-menu.css shows the sheet below md; from there up it is display:none and
// the docked menu is the editor.
const isPhone = () => window.innerWidth < 768

export default class extends Controller {
  static targets = ["sheet", "scrim", "toggle"]
  static values = { editing: Boolean }

  declare readonly editingValue: boolean

  declare readonly sheetTarget: HTMLElement
  declare readonly scrimTarget: HTMLElement
  declare readonly toggleTargets: HTMLElement[]
  declare readonly hasSheetTarget: boolean

  private isOpen = false
  private dragging = false
  private dragStartY = 0
  private dragDelta = 0

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleBeforeCache = this.handleBeforeCache.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)

    // Phones only: opening pins the page, which from md up would freeze the
    // docked editor behind a sheet nobody can see. A frame late, so the
    // slide-in runs from the sheet's parked position.
    if (this.editingValue) {
      this.handleResize = this.handleResize.bind(this)
      window.addEventListener("resize", this.handleResize, { passive: true })
      if (isPhone()) requestAnimationFrame(() => this.open())
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    window.removeEventListener("resize", this.handleResize)
    unlockPage(this.element)
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.isOpen || !this.hasSheetTarget) return

    this.isOpen = true
    lockPage(this.element)
    this.sheetTarget.hidden = false
    this.scrimTarget.hidden = false
    this.sheetTarget.offsetHeight // reflow, so the slide starts from off screen
    this.sheetTarget.classList.add("is-open")
    this.scrimTarget.classList.add("is-open")
    this.syncToggles()
    this.dispatch("opened", { bubbles: true })
  }

  // Editing holds the sheet open; Done is the way out.
  close() {
    if (!this.editingValue) this.dismiss()
  }

  private dismiss() {
    if (!this.isOpen) return

    this.isOpen = false
    unlockPage(this.element)
    this.sheetTarget.classList.remove("is-open")
    this.scrimTarget.classList.remove("is-open")
    this.syncToggles()
    this.dispatch("closed", { bubbles: true })
    setTimeout(() => {
      if (this.isOpen) return
      this.sheetTarget.hidden = true
      this.scrimTarget.hidden = true
    }, SLIDE_MS)
  }

  // ---- swipe-to-dismiss, as on the sheet frame drawer (modal_controller) ----

  dragStart(event: PointerEvent) {
    if (!this.isOpen) return
    this.dragging = true
    this.dragStartY = event.clientY
    this.dragDelta = 0
    ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
    this.sheetTarget.style.transition = "none"
  }

  dragMove(event: PointerEvent) {
    if (!this.dragging) return
    // Downward only; the sheet never lifts above its resting place.
    this.dragDelta = Math.max(0, event.clientY - this.dragStartY)
    this.sheetTarget.style.transform = `translateY(${this.dragDelta}px)`
  }

  dragEnd() {
    if (!this.dragging) return
    this.dragging = false
    this.sheetTarget.style.transition = ""
    const threshold = Math.min(this.sheetTarget.offsetHeight * 0.3, 140)
    // Clearing the inline transform hands control back to the class: past the
    // threshold, removing `is-open` slides down from the dragged position;
    // short of it, the sheet eases back up.
    this.sheetTarget.style.transform = ""
    if (this.dragDelta > threshold) this.close()
  }

  private syncToggles() {
    this.toggleTargets.forEach((toggle) => toggle.setAttribute("aria-expanded", String(this.isOpen)))
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") this.close()
  }

  // Crossing md swaps editors: the docked menu takes over and the sheet lets
  // the page go; back below it, the sheet resumes.
  private handleResize() {
    if (isPhone()) this.open()
    else this.dismiss()
  }

  // Turbo snapshots the body as it is: the cached page must not restore open.
  private handleBeforeCache() {
    if (!this.hasSheetTarget) return

    this.isOpen = false
    unlockPage(this.element)
    this.sheetTarget.classList.remove("is-open")
    this.scrimTarget.classList.remove("is-open")
    this.sheetTarget.hidden = true
    this.scrimTarget.hidden = true
    this.syncToggles()
  }
}
